import React, { useMemo } from "react";
import { Form } from "react-bootstrap";
import Collapsible from "react-collapsible";
import paged from "../../../../../enhancers/paged";

interface Column<T> {
  title: string;
  key: string;
  render: (row: T) => React.ReactNode;
}

interface ApkTableProps<T> {
  data: T[];
  columns: Column<T>[];
  emptyMessage?: string;
  filterPredicate?: (row: T, filter: string) => boolean | undefined;
  filter?: string;
  filterPlaceholder?: string;
}

interface TableProps<T> extends ApkTableProps<T> {
  data: T[];
  columns: Column<T>[];
  pageSize: number;
  onFilterChange?: (value: string) => void;
  onPageChange?: (page: number, pageSize: number) => void;
}

function Table<T>({
  data,
  columns,
  emptyMessage = "No data available.",
  filter,
  filterPlaceholder = "Search...",
  onFilterChange,
  onPageChange,
  pageSize
}: Omit<TableProps<T>, "filterPredicate">) {
  return (
    <table className="simple-table">
      {(columns.length > 1 || onFilterChange) && (
        <thead>
          {onFilterChange && (
            <tr>
              <th colSpan={columns.length} style={{ padding: "0.5rem" }}>
                <Form.Control
                  style={{
                    borderRadius: "3px",
                    minHeight: "38px",
                    fontSize: "1rem"
                  }}
                  type="text"
                  placeholder={filterPlaceholder}
                  value={filter}
                  onChange={e => {
                    onFilterChange?.(e.target.value);
                    onPageChange?.(1, pageSize);
                  }}
                />
              </th>
            </tr>
          )}
          {columns.length > 1 && (
            <tr>
              {columns.map(col => (
                <th key={col.key}>{col.title}</th>
              ))}
            </tr>
          )}
        </thead>
      )}
      <tbody>
        {data.length === 0 ? (
          <tr className="simple-table__empty">
            <td colSpan={columns.length}>{emptyMessage}</td>
          </tr>
        ) : (
          data.map((row, idx) => (
            <tr key={idx}>
              {columns.map(col => (
                <td key={col.key}>{col.render(row)}</td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}

const PagedTable = paged(Table, "data");

interface CollapsibleTableProps<T> extends ApkTableProps<T> {
  title: (data: T[]) => string;
  children?: React.ReactNode;
}

export default function CollapsibleTable<T>(props: CollapsibleTableProps<T>) {
  const { title, children, data, filterPredicate, ...rest } = props;
  const [filter, setFilter] = React.useState("");
  const [isOpen, setIsOpen] = React.useState(false);

  const handleOpening = () => {
    setIsOpen(true);
  };

  const handleClosing = () => {
    setIsOpen(false);
    setFilter("");
  };

  const filteredData = useMemo(
    () =>
      filter && filterPredicate
        ? data.filter(row => filterPredicate(row, filter))
        : data,
    [data, filter, filterPredicate]
  );

  return (
    <Collapsible
      trigger={
        <div className="react-collapsible__heading-container">
          <span className="react-collapsible__heading-title">
            {title(isOpen ? filteredData : data)}
          </span>
        </div>
      }
      classParentString="react-collapsible"
      transitionTime={200}
      easing="ease-out"
      lazyRender
      onOpening={handleOpening}
      onClosing={handleClosing}
    >
      {children || (
        <PagedTable
          {...rest}
          data={filteredData}
          filter={filter}
          onFilterChange={setFilter}
          filterPredicate={filterPredicate}
          columns={props.columns}
          initialPageSize={10}
        />
      )}
    </Collapsible>
  );
}
